import { useState } from 'react'
import { Printer } from 'lucide-react'
import type { CreatedProduct } from '@/types/products'
import {
  PRODUCT_LABEL_TYPES,
  SELECT_MESSAGE_FIELDS,
  type ProductLabelSelections,
  type ProductLabelType,
} from '@/lib/digitrax'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'

/** HemaTraxCT caps MFG Text lines at 35 characters (fields 45–48). */
const MFG_TEXT_MAX = 35

/**
 * The Label Printing popup shown after a product is created (spec Image 1):
 * label type, four "Select Message" fields, four free-text lines. The mock
 * draws Select Message as plain text boxes, so these are free-typed inputs —
 * with a datalist of the HemaTraxCT selection options as suggestions, since
 * the printer constrains those fields to its selection tables (an open
 * question with Digi-Trax; see the ticket).
 *
 * Print Label advances via `onPrinted` with the selections: the page shows the
 * label preview next, where the operator signs the pre-print verification and
 * the label is recorded, sent to the (stubbed) DigiTrax transport and printed
 * (z8nrz7dt48). Nothing is sent or recorded before that signature.
 */
export function LabelPrintingDialog({
  product,
  onClose,
  onPrinted,
}: {
  product: CreatedProduct | null
  onClose: () => void
  onPrinted: (product: CreatedProduct, selections: ProductLabelSelections) => void
}) {
  const [labelType, setLabelType] = useState<ProductLabelType>('Standard')
  const [selects, setSelects] = useState<Record<string, string>>({})
  const [freeText, setFreeText] = useState<string[]>(['', '', '', ''])

  function reset() {
    setLabelType('Standard')
    setSelects({})
    setFreeText(['', '', '', ''])
  }

  function handlePrint() {
    if (!product) return
    const selections: ProductLabelSelections = {
      labelType,
      bloodType: selects.bloodType || undefined,
      specialMessage: selects.specialMessage || undefined,
      intendedUse: selects.intendedUse || undefined,
      donationType: selects.donationType || undefined,
      freeText: [
        freeText[0] || undefined,
        freeText[1] || undefined,
        freeText[2] || undefined,
        freeText[3] || undefined,
      ],
    }
    reset()
    // The selections go WITH the product: the preview that follows has to
    // show what was just typed, or it is confirming a different label.
    onPrinted(product, selections)
  }

  return (
    <Dialog
      open={!!product}
      onOpenChange={o => {
        if (!o) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className='sm:max-w-2xl'>
        <DialogHeader>
          <DialogTitle>Label Printing</DialogTitle>
          <DialogDescription>
            {product ? `DIN ${product.din}` : ''} — select the label type and messages, then print.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2'>
          <p className='text-sm font-medium text-muted-foreground'>Select the Label Type</p>
          <div className='space-y-2'>
            {PRODUCT_LABEL_TYPES.map(type => (
              <label key={type} className='flex w-fit cursor-pointer items-center gap-2 text-sm'>
                <Checkbox
                  checked={labelType === type}
                  onCheckedChange={checked => {
                    if (checked) setLabelType(type)
                  }}
                  aria-label={type}
                />
                {type}
              </label>
            ))}
          </div>
        </div>

        <div className='grid grid-cols-1 gap-6 sm:grid-cols-2'>
          <div className='space-y-2'>
            <p className='text-sm font-medium'>Select Message</p>
            {SELECT_MESSAGE_FIELDS.map(({ key, label, options }) => (
              <div key={key}>
                <Input
                  value={selects[key] ?? ''}
                  placeholder={label}
                  aria-label={label}
                  list={`select-message-${key}`}
                  onChange={e => setSelects(prev => ({ ...prev, [key]: e.target.value }))}
                />
                <datalist id={`select-message-${key}`}>
                  {options.map(o => (
                    <option key={o} value={o} />
                  ))}
                </datalist>
              </div>
            ))}
          </div>
          <div className='space-y-2'>
            <p className='text-sm font-medium'>Free Text Message</p>
            {freeText.map((value, i) => (
              <Input
                key={i}
                value={value}
                maxLength={MFG_TEXT_MAX}
                aria-label={`Free text message ${i + 1}`}
                onChange={e =>
                  setFreeText(prev => prev.map((v, j) => (j === i ? e.target.value : v)))
                }
              />
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant='outline'
            onClick={() => {
              reset()
              onClose()
            }}
          >
            Cancel
          </Button>
          <Button onClick={handlePrint} disabled={!product}>
            <Printer className='mr-2 h-4 w-4' />
            Print Label
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
